<script module>
  import { openOverpanel, closeOverpanel, toggleOverpanel } from '../../stores/overpanelControl.ts';

  export const open = openOverpanel;
  export const close = closeOverpanel;
  export const toggle = toggleOverpanel;
</script>

<script>
  import { onMount, onDestroy } from 'svelte';
  import UseEscapeKeydown from '../../utils/UseEscapeKeydown.svelte';
  import UseFocusTrap from '../../utils/UseFocusTrap.svelte';
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import {
    focusLastOverpanel,
    getOverpanelIndex,
    overpanelMap,
    setOverpanelNodeRef,
  } from '../../stores/overpanelControl.ts';
  
  let {
    title,
    overpanelKey,
    mountCallback = () => {},
    closeCallback = () => {},
    children
  } = $props();

  if (!overpanelKey) {
    throw new Error('OverpanelControl passed falsey overpanelKey prop');
  }

  const onClose = () => {
    closeOverpanel(overpanelKey);
  };

  let overpanelContainer = $state();
  let container = $state();
  let overrideCloseCallback;
  let windowInnerHeight = $state();

  $effect(() => {
    if (windowInnerHeight) {
      if (overpanelContainer && overpanelContainer.style) {
        overpanelContainer.style.height = windowInnerHeight + 'px';
      }

      if (container && container.style) {
        container.style.height = windowInnerHeight + 'px';
      }
    }
  });

  onMount(() => {
    setOverpanelNodeRef(overpanelKey, overpanelContainer);
    focusLastOverpanel();
    mountCallback();
    overrideCloseCallback = $overpanelMap.get(overpanelKey).closeCallback;
    return () => {
      focusLastOverpanel();
    };
  });

  onDestroy(() => {
    if (overrideCloseCallback) {
      overrideCloseCallback();
    } else {
      closeCallback();
    }
  });
</script>

<svelte:window bind:innerHeight={windowInnerHeight} />

{#if overpanelContainer}
  <UseEscapeKeydown container={overpanelContainer} onEscapeHandler={onClose} />
  <UseFocusTrap container={overpanelContainer} />
{/if}
<div
  tabindex="-1"
  bind:this={overpanelContainer}
  class="fixed top-0 left-0 w-full focus:outline-none z-20"
  style={`z-index: ${50 + getOverpanelIndex(overpanelKey) * 10}`}
  data-testid="overpanel-control"
>
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div
    role="button"
    tabindex="-1"
    onclick={onClose}
    class="absolute w-full h-screen top-0 left-0 z-10 {getOverpanelIndex(overpanelKey) >= 1
      ? 'bg-transparent'
      : 'bg-black opacity-70'}"
  ></div>
  <div class="animated-fly-in absolute w-full max-w-lg top-0 right-0 z-50 h-screen">
    <div class="flex flex-col h-full" bind:this={container}>
      <div class="flex justify-between items-center min-h-14 px-4 py-2 bg-blue-navy">
        <div class="flex items-center">
          {#if $overpanelMap.get(overpanelKey) && $overpanelMap.get(overpanelKey).useChevronIcon}
            <button
              class="p-1 w-8 h-8 mr-3 hover-hover:hover:bg-blue-darkish active:bg-blue-darkish focus:outline-none focus:bg-blue-darkish focus:shadow-focus active:shadow-active"
              onclick={onClose}
              aria-label="Close overpanel"
              data-testid="overpanel-control-close"
            >
              <svg class="w-full h-full text-white fill-current rotate-180">
                <use xlink:href={`${SPRITESHEET_URL}#chevron-right`} />
              </svg>
            </button>
          {/if}
          <h1 class="leading-tight text-white text-1.5xl">{title}</h1>
        </div>
        {#if $overpanelMap.get(overpanelKey) && !$overpanelMap.get(overpanelKey).useChevronIcon}
          <button
            class="p-1 w-8 h-8 shrink-0 hover-hover:hover:bg-blue-darkish active:bg-blue-darkish focus:outline-none focus:bg-blue-darkish focus:shadow-focus active:shadow-active"
            onclick={onClose}
            aria-label="Close overpanel"
            data-testid="overpanel-control-close"
          >
            <svg class="w-full h-full text-white fill-current">
              <use xlink:href={`${SPRITESHEET_URL}#close-x`} />
            </svg>
          </button>
        {/if}
      </div>
      <section class="flex-1 overflow-y-auto allow-tab">
        {@render children?.()}
      </section>
    </div>
  </div>
</div>
